// src/pages/FieldInterventions/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  Pagination,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CompleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import { useSiteFilter } from '../../contexts/SiteFilterContext';
import { useIsMobile } from '../../hooks/useResponsive';
import { fieldInterventionsApi } from '../../services/apiClient';
import { usePersistedFilter, FILTER_KEYS } from '../../utils/filterPersistence';

const FieldInterventions = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { t } = useThemeSettings();
  const isMobile = useIsMobile();
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();

  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use persisted filters
  const [searchFilter, setSearchFilter] = usePersistedFilter(FILTER_KEYS.FIELD_INTERVENTION.SEARCH, '');
  const [statusFilter, setStatusFilter] = usePersistedFilter(FILTER_KEYS.FIELD_INTERVENTION.STATUS_FILTER, '');
  const [outcomeFilter, setOutcomeFilter] = usePersistedFilter(FILTER_KEYS.FIELD_INTERVENTION.OUTCOME_FILTER, '');
  const [locationFilter, setLocationFilter] = usePersistedFilter(FILTER_KEYS.FIELD_INTERVENTION.LOCATION_FILTER, '');
  const [showUninvoicedOnly, setShowUninvoicedOnly] = usePersistedFilter(FILTER_KEYS.FIELD_INTERVENTION.SHOW_UNINVOICED_ONLY, false);
  const [currentPage, setCurrentPage] = usePersistedFilter(FILTER_KEYS.FIELD_INTERVENTION.PAGE, 1);
  const [pageLimit, setPageLimit] = usePersistedFilter(FILTER_KEYS.FIELD_INTERVENTION.LIMIT, 20);

  const [pagination, setPagination] = useState({
    total: 0,
    page: currentPage,
    limit: pageLimit,
    totalPages: 1
  });

  // Combine into filters object for backward compatibility
  const filters = {
    status: statusFilter,
    outcome: outcomeFilter,
    location_id: locationFilter,
    search: searchFilter,
    showUninvoicedOnly: showUninvoicedOnly
  };

  // Check permissions
  const canCreate = hasPermission('field_interventions.create');
  const canEdit = hasPermission('field_interventions.edit');
  const canViewAll = hasPermission('field_interventions.view_all');
  const isAdmin = user?.role === 'admin';

  // Load interventions - use currentPage and pageLimit from persisted state to avoid loop
  const loadInterventions = useCallback(async () => {
    try {
      setLoading(true);

      // Build query params object - use persisted state values, not pagination state
      const params = {
        page: currentPage,
        limit: pageLimit
      };
      if (filters.status) params.status = filters.status;
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.search) params.search = filters.search;

      // Uninvoiced filter: show only billable interventions not yet invoiced
      if (filters.showUninvoicedOnly) {
        params.outcome = 'not_included';
        params.invoiced = false;
      } else if (filters.outcome) {
        // Only apply manual outcome filter if uninvoiced filter is not active
        params.outcome = filters.outcome;
      }

      // Use the API client with proper token handling
      const response = await fieldInterventionsApi.getAll(params);
      let interventionsData = response.data.interventions || [];

      // Filter by site - when hasSiteFilter is true and siteLocationIds is set (even if empty array for no-access users)
      if (hasSiteFilter && Array.isArray(siteLocationIds)) {
        interventionsData = interventionsData.filter(intervention => {
          const interventionLocationId = intervention.location_id || intervention.locationId || intervention.location?.id;
          return siteLocationIds.includes(Number(interventionLocationId)) || siteLocationIds.includes(String(interventionLocationId));
        });
      }

      setInterventions(interventionsData);

      // Update pagination from response (adjust if filtered)
      if (response.data.pagination) {
        const paginationData = { ...response.data.pagination };
        if (hasSiteFilter && Array.isArray(siteLocationIds)) {
          paginationData.total = interventionsData.length;
          paginationData.totalPages = Math.ceil(interventionsData.length / paginationData.limit);
        }
        setPagination(paginationData);
      }
    } catch (error) {
      console.error('Error loading interventions:', error);
      enqueueSnackbar(
        t ? t('FieldInterventions.error_loading') : 'Failed to load interventions',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageLimit, statusFilter, outcomeFilter, locationFilter, searchFilter, showUninvoicedOnly, enqueueSnackbar, t, hasSiteFilter, siteLocationIds]);

  useEffect(() => {
    loadInterventions();
  }, [loadInterventions]);

  const handleFilterChange = (field, value) => {
    // Update individual filter states
    switch(field) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'outcome':
        setOutcomeFilter(value);
        break;
      case 'location_id':
        setLocationFilter(value);
        break;
      case 'search':
        setSearchFilter(value);
        break;
      case 'showUninvoicedOnly':
        setShowUninvoicedOnly(value);
        break;
      default:
        break;
    }
    // Reset to page 1 when filters change
    setCurrentPage(1);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    setCurrentPage(1);
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleCreateIntervention = () => {
    navigate('/field-interventions/new');
  };

  const handleViewIntervention = (id) => {
    navigate(`/field-interventions/${id}`);
  };

  const handleEditIntervention = (id) => {
    navigate(`/field-interventions/${id}`);
  };

  const handleDeleteIntervention = async (id) => {
    if (!window.confirm(t ? t('FieldInterventions.confirm_delete') : 'Are you sure you want to delete this intervention?')) {
      return;
    }

    try {
      await fieldInterventionsApi.delete(id);

      enqueueSnackbar(
        t ? t('FieldInterventions.intervention_deleted') : 'Intervention deleted successfully',
        { variant: 'success' }
      );
      loadInterventions();
    } catch (error) {
      console.error('Error deleting intervention:', error);
      enqueueSnackbar(
        t ? t('FieldInterventions.error_deleting') : 'Failed to delete intervention',
        { variant: 'error' }
      );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getOutcomeColor = (outcome, invoiced = false) => {
    switch (outcome) {
      case 'included_in_contract':
        return 'success';
      case 'not_included':
        // Green if invoiced, yellow if not yet invoiced
        return invoiced ? 'success' : 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Render mobile card view
  const renderMobileView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : interventions.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {t ? t('FieldInterventions.no_interventions_found') : 'No interventions found'}
          </Typography>
        </Paper>
      ) : (
        interventions.map((intervention) => (
          <Card key={intervention.id} sx={{ width: '100%' }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="h6" component="div">
                    {intervention.location?.friendlyName || 'Unknown Location'}
                  </Typography>
                  <Chip
                    label={t ? t(`FieldInterventions.status_${intervention.status || 'pending'}`) : (intervention.status || 'Pending')}
                    color={getStatusColor(intervention.status || 'pending')}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t ? t('FieldInterventions.intervention_date') : 'Date'}:</strong> {formatDate(intervention.intervention_date)}
                  </Typography>
                </Box>

                {intervention.outcome && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" component="span">
                      <strong>{t ? t('FieldInterventions.outcome') : 'Outcome'}:</strong>{' '}
                    </Typography>
                    <Chip
                      label={t ? t(`FieldInterventions.outcome_${intervention.outcome}`) : intervention.outcome}
                      color={getOutcomeColor(intervention.outcome, intervention.invoiced)}
                      size="small"
                    />
                  </Box>
                )}

                {intervention.ticket && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t ? t('FieldInterventions.ticket') : 'Ticket'}:</strong> #{intervention.ticket.id}
                  </Typography>
                )}

                {intervention.supportUser && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t ? t('FieldInterventions.support_user') : 'Support'}:</strong> {intervention.supportUser.username}
                  </Typography>
                )}
              </Stack>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
              <Button
                size="small"
                startIcon={<ViewIcon />}
                onClick={() => handleViewIntervention(intervention.id)}
              >
                {t ? t('FieldInterventions.view_details') : 'View'}
              </Button>
              {canEdit && intervention.status !== 'completed' && intervention.status !== 'cancelled' && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditIntervention(intervention.id)}
                >
                  {t ? t('FieldInterventions.edit') : 'Edit'}
                </Button>
              )}
              {isAdmin && (
                <IconButton
                  size="small"
                  onClick={() => handleDeleteIntervention(intervention.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </CardActions>
          </Card>
        ))
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
              {t ? t('FieldInterventions.page_title') : 'Field Intervention Reports'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t ? t('FieldInterventions.page_subtitle') : 'Manage field intervention reports and track on-site work'}
            </Typography>
          </Box>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateIntervention}
              fullWidth={isMobile}
              size={isMobile ? "medium" : "medium"}
            >
              {t ? t('FieldInterventions.new_intervention') : 'New Intervention'}
            </Button>
          )}
        </Box>

        {/* Quick Filter: Uninvoiced Only */}
        <Box sx={{ mb: 2 }}>
          <Chip
            label={
              filters.showUninvoicedOnly
                ? (t ? t('FieldInterventions.showing_uninvoiced_only') : '✓ Showing Uninvoiced Only')
                : (t ? t('FieldInterventions.show_uninvoiced_only') : 'Show Uninvoiced Only')
            }
            color={filters.showUninvoicedOnly ? 'warning' : 'default'}
            variant={filters.showUninvoicedOnly ? 'filled' : 'outlined'}
            onClick={() => handleFilterChange('showUninvoicedOnly', !filters.showUninvoicedOnly)}
            clickable
            sx={{
              fontWeight: filters.showUninvoicedOnly ? 600 : 400,
              fontSize: { xs: '0.875rem', sm: '0.8125rem' },
              height: { xs: 36, sm: 32 },
              '& .MuiChip-label': {
                px: { xs: 1.5, sm: 1.25 },
              },
            }}
          />
          {filters.showUninvoicedOnly && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {t ? t('FieldInterventions.uninvoiced_filter_active') : 'Showing only billable interventions that need invoicing'}
            </Typography>
          )}
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label={t ? t('FieldInterventions.filter_by_status') : 'Filter by Status'}
                select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">{t ? t('FieldInterventions.all_statuses') : 'All Statuses'}</MenuItem>
                <MenuItem value="pending">{t ? t('FieldInterventions.status_pending') : 'Pending'}</MenuItem>
                <MenuItem value="in_progress">{t ? t('FieldInterventions.status_in_progress') : 'In Progress'}</MenuItem>
                <MenuItem value="completed">{t ? t('FieldInterventions.status_completed') : 'Completed'}</MenuItem>
                <MenuItem value="cancelled">{t ? t('FieldInterventions.status_cancelled') : 'Cancelled'}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label={t ? t('FieldInterventions.filter_by_outcome') : 'Filter by Outcome'}
                select
                value={filters.outcome}
                onChange={(e) => handleFilterChange('outcome', e.target.value)}
              >
                <MenuItem value="">{t ? t('FieldInterventions.all_outcomes') : 'All Outcomes'}</MenuItem>
                <MenuItem value="included_in_contract">{t ? t('FieldInterventions.outcome_included_in_contract') : 'Included in Contract'}</MenuItem>
                <MenuItem value="not_included">{t ? t('FieldInterventions.outcome_not_included') : 'Not Included'}</MenuItem>
                <MenuItem value="cancelled">{t ? t('FieldInterventions.outcome_cancelled') : 'Cancelled'}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                fullWidth
                label={t ? t('FieldInterventions.search_interventions') : 'Search interventions...'}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Interventions Table/Cards */}
        {isMobile ? (
          renderMobileView()
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t ? t('FieldInterventions.intervention_date') : 'Date'}</TableCell>
                  <TableCell>{t ? t('FieldInterventions.location') : 'Location'}</TableCell>
                  <TableCell>{t ? t('FieldInterventions.status') : 'Status'}</TableCell>
                  <TableCell>{t ? t('FieldInterventions.outcome') : 'Outcome'}</TableCell>
                  <TableCell>{t ? t('FieldInterventions.ticket') : 'Ticket'}</TableCell>
                  <TableCell>{t ? t('FieldInterventions.support_user') : 'Support'}</TableCell>
                  <TableCell align="right">{t ? t('FieldInterventions.actions') : 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : interventions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t ? t('FieldInterventions.no_interventions_found') : 'No interventions found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  interventions.map((intervention) => (
                    <TableRow key={intervention.id} hover>
                      <TableCell>{formatDate(intervention.intervention_date)}</TableCell>
                      <TableCell>{intervention.location?.friendlyName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={t ? t(`FieldInterventions.status_${intervention.status || 'pending'}`) : (intervention.status || 'Pending')}
                          color={getStatusColor(intervention.status || 'pending')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {intervention.outcome ? (
                          <Chip
                            label={t ? t(`FieldInterventions.outcome_${intervention.outcome}`) : intervention.outcome}
                            color={getOutcomeColor(intervention.outcome, intervention.invoiced)}
                            size="small"
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {intervention.ticket ? `#${intervention.ticket.id}` : '-'}
                      </TableCell>
                      <TableCell>{intervention.supportUser?.username || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleViewIntervention(intervention.id)}
                          title={t ? t('FieldInterventions.view_details') : 'View Details'}
                        >
                          <ViewIcon />
                        </IconButton>
                        {canEdit && intervention.status !== 'completed' && intervention.status !== 'cancelled' && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditIntervention(intervention.id)}
                            title={t ? t('FieldInterventions.edit') : 'Edit'}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {isAdmin && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteIntervention(intervention.id)}
                            color="error"
                            title={t ? t('FieldInterventions.delete') : 'Delete'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination Controls */}
        {!loading && interventions.length > 0 && (
          <Paper sx={{ p: 2, mt: 3 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2
            }}>
              {/* Items per page selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t ? t('FieldInterventions.rows_per_page') : 'Rows per page:'}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pagination.limit}
                    onChange={(e) => handleLimitChange(e.target.value)}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Page info and pagination */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                <Typography variant="body2" color="text.secondary">
                  {t ? t('FieldInterventions.showing_results') : 'Showing'} {((pagination.page - 1) * pagination.limit) + 1}-
                  {Math.min(pagination.page * pagination.limit, pagination.total)} {t ? t('FieldInterventions.of') : 'of'} {pagination.total}
                </Typography>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={(e, page) => handlePageChange(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default FieldInterventions;
